import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { get } from '../lib/api.ts';
import { useSession, useWs } from '../lib/session.tsx';
import { Icon, type IconName } from '../components/Icons.tsx';
import { Badge, cx, IconButton, Select } from '../components/ui.tsx';

const NAV: Array<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: '/', label: 'Overview', icon: 'home', end: true },
  { to: '/campaigns', label: 'Campaigns', icon: 'megaphone', end: true },
  { to: '/campaigns/new', label: 'New Voice Campaign', icon: 'plus' },
  { to: '/contacts', label: 'Contacts & Groups', icon: 'users' },
  { to: '/audio', label: 'Audio Library', icon: 'wave' },
  { to: '/reports', label: 'Reports', icon: 'chart' },
  { to: '/settings', label: 'Settings', icon: 'gear' },
  { to: '/account', label: 'My Account', icon: 'user' },
];

export interface Integrations {
  deliveryMode: 'simulation' | 'live';
  voice: { live: { ready: boolean; missing: string[] } };
}

export function AppShell() {
  const { me, workspaceId, setWorkspace, signOut, role } = useSession();
  const ws = useWs();
  const [open, setOpen] = useState(false);
  const _loc = useLocation();
  useEffect(() => setOpen(false), []);
  const integ = useQuery({
    queryKey: ['integrations', workspaceId],
    queryFn: () => get<Integrations>(`${ws}/settings/integrations`),
    refetchInterval: 60_000,
  });
  const overview = useQuery({
    queryKey: ['halt', workspaceId],
    queryFn: () => get<{ emergencyStop: string | null }>(`${ws}/overview`),
    refetchInterval: 30_000,
  });
  const mode = integ.data?.deliveryMode ?? 'simulation';

  const nav = (
    <nav aria-label="Main">
      <ul className="space-y-1">
        {NAV.map((n) => (
          <li key={n.to}>
            <NavLink
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium',
                  isActive ? 'bg-navy-700 text-white' : 'text-navy-100 hover:bg-navy-800 hover:text-white',
                )
              }
            >
              <Icon name={n.icon} className="h-5 w-5" />
              {n.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );

  return (
    <div className="min-h-screen md:grid md:grid-cols-[16rem_1fr]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2"
      >
        Skip to content
      </a>
      <aside
        className={cx(
          'print:hidden fixed inset-y-0 start-0 z-40 w-64 flex-col bg-navy-900 p-4 md:static md:flex',
          open ? 'flex' : 'hidden',
        )}
      >
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <img src="/favicon.svg" alt="" className="h-8 w-8" />
            <span className="font-semibold">{me?.appName ?? 'Broadcast System'}</span>
          </div>
          <IconButton
            icon="close"
            label="Close menu"
            className="text-white md:hidden"
            onClick={() => setOpen(false)}
          />
        </div>
        {nav}
        <div className="mt-auto space-y-3 pt-6 text-sm text-navy-100">
          {me && me.workspaces.length > 1 && (
            <label className="block">
              <span className="mb-1 block text-xs text-navy-200">Workspace</span>
              <Select
                value={workspaceId ?? ''}
                onChange={(e) => setWorkspace(e.target.value)}
                className="bg-navy-800 text-white"
              >
                {me.workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            </label>
          )}
          <p className="truncate" title={me?.user.email}>
            {me?.user.displayName || me?.user.email} <span className="text-navy-300">· {role}</span>
          </p>
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-navy-800"
          >
            <Icon name="logout" className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      {open && (
        <div
          className="fixed inset-0 z-30 bg-navy-950/50 md:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      <div className="min-w-0">
        <header className="print:hidden sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/90 px-4 py-2 backdrop-blur dark:border-navy-800 dark:bg-navy-950/90">
          <IconButton icon="menu" label="Open menu" className="md:hidden" onClick={() => setOpen(true)} />
          <span className="truncate text-sm text-slate-600 dark:text-slate-300">
            {me?.workspaces.find((w) => w.id === workspaceId)?.name}
          </span>
          <span className="ms-auto">
            {mode === 'simulation' ? (
              <Badge kind="simulation">Simulation mode: no real calls</Badge>
            ) : (
              <Badge kind="live">LIVE: real calls</Badge>
            )}
          </span>
        </header>
        {overview.data?.emergencyStop && (
          <div
            role="alert"
            className="flex items-center gap-2 bg-red-700 px-4 py-2 text-sm font-medium text-white"
          >
            <Icon name="stop" className="h-4 w-4" /> Emergency stop is ON: no new calls will start. Lift it in
            Settings → Safety.
          </div>
        )}
        <main id="main" tabIndex={-1} className="mx-auto max-w-7xl p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
